import { forwardRef } from "react";
import styles from "./Button.module.css";

/**
 * Button primitive aligned to Figma component set "Button" (node 277:5423).
 * Figma variant axes: Color (Brand secondary / White, White Sage, Grey), State (Default, Active, Inactive),
 * Size (Default, Small), Type (Text, Text + Icon, Icon, Ico).
 *
 * Pass `href` to render the same visual as an anchor (used for outbound product links
 * so they behave like real links: middle-click, long-press, "open in new tab").
 *
 * @typedef {'primary' | 'secondary'} ButtonVariant maps to Figma Color Brand secondary / White | White Sage
 * @typedef {'md' | 'sm'} ButtonSize maps to Figma Default | Small
 * @typedef {'text' | 'textIcon' | 'icon'} ButtonLayout maps to Figma Text | Text + Icon | Icon / Ico
 */

const COLORS = {
  brandSecondary: {
    default: {
      "--btn-bg": "var(--gg-brand-secondary)",
      "--btn-fg": "var(--gg-white)",
      "--btn-inset": "none",
    },
    selected: {
      "--btn-bg": "var(--gg-midnight)",
      "--btn-fg": "var(--gg-brand-secondary)",
      "--btn-inset": "none",
    },
    disabled: {
      "--btn-bg": "var(--gg-grey-surface)",
      "--btn-fg": "var(--gg-grey-muted)",
      "--btn-inset": "none",
    },
  },
  surface: {
    default: {
      "--btn-bg": "var(--gg-white)",
      "--btn-fg": "var(--gg-midnight)",
      "--btn-inset": "none",
    },
    selected: {
      "--btn-bg": "var(--gg-soft-sage)",
      "--btn-fg": "var(--gg-midnight)",
      "--btn-inset": "inset 0 0 0 2px var(--gg-white)",
    },
    disabled: {
      "--btn-bg": "var(--gg-grey-surface)",
      "--btn-fg": "var(--gg-grey-muted)",
      "--btn-inset": "none",
    },
  },
};

function resolveVisualState(selected, disabled) {
  if (disabled) return "disabled";
  if (selected) return "selected";
  return "default";
}

const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    layout = "text",
    label,
    icon,
    selected = false,
    disabled = false,
    fullWidth = false,
    className,
    children,
    type = "button",
    href,
    target,
    rel,
    ...rest
  },
  ref,
) {
  const scheme = variant === "secondary" ? "surface" : "brandSecondary";
  const visualState = resolveVisualState(selected, disabled);
  const style = COLORS[scheme][visualState];

  const stringChild =
    typeof children === "string" || typeof children === "number"
      ? children
      : null;
  const textFromLabel = label ?? stringChild;

  const rootClass = [
    styles.root,
    fullWidth && styles.fullWidth,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content =
    layout === "icon" ? (
      <span className={styles.iconSlot}>{icon ?? children}</span>
    ) : layout === "textIcon" ? (
      <>
        {textFromLabel != null && textFromLabel !== "" ? (
          <span className={styles.label}>{textFromLabel}</span>
        ) : null}
        {icon ? <span className={styles.iconSlot}>{icon}</span> : null}
      </>
    ) : (
      <span className={styles.label}>{textFromLabel ?? children}</span>
    );

  if (href && !disabled) {
    return (
      <a
        ref={ref}
        href={href}
        target={target}
        rel={target === "_blank" ? (rel ?? "noopener noreferrer") : rel}
        className={rootClass}
        style={style}
        data-layout={layout}
        data-size={size}
        {...rest}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={rootClass}
      style={style}
      data-layout={layout}
      data-size={size}
      disabled={disabled}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
